import { authenticateInTeams } from "../teams";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Opens a Microsoft consent page in the Teams popup and answers whether the consent was completed.
 *
 * The API records the grant as soon as Microsoft redirects to its callback, whatever happens to the popup: Microsoft's pages can cut
 * the link between Teams and the popup (Cross-Origin-Opener-Policy), so the popup may never report back. The caller's `isDone`
 * (a fresh read of the status from the API) is therefore asked every two seconds, and also once when the popup ends. The first
 * positive answer wins; a popup that ends without consent answers false.
 */
export async function runConsent(
  url: string,
  isDone: () => Promise<boolean>,
  { pollMs = 2000, maxMs = 5 * 60_000 }: { pollMs?: number; maxMs?: number } = {},
): Promise<boolean> {
  let stop = false;
  const poll = (async () => {
    const t0 = Date.now();
    while (!stop && Date.now() - t0 < maxMs) {
      await sleep(pollMs);
      if (stop) break;
      if (await isDone().catch(() => false)) return true;
    }
    return false;
  })();
  const popup = authenticateInTeams(url).then(
    () => true,
    () => false,
  );
  const ended = popup.then(async () => {
    if (await isDone().catch(() => false)) return true;
    await sleep(Math.min(1500, pollMs)); // the callback may be completing just now
    return isDone().catch(() => false);
  });
  const ok = await Promise.race([poll, ended]);
  stop = true;
  return ok;
}
